import { getGuestId } from './identity'

const gameStateKey = 'gameState'
const archiveGameStateKey = 'archiveGameState'
const highContrastKey = 'highContrast'

export type StoredGameState = {
  guesses: string[]
  solution: string
}

// Scope the board cache per identity: `gameState:<guest_id>` when signed in,
// else the legacy un-scoped `gameState` for anonymous/dev play. This keeps one
// guest's locally-cached board from showing when a different link (identity) is
// opened in the same browser — each identity reads/writes its own key.
const scopedKey = (base: string): string => {
  const id = getGuestId()
  return id ? `${base}:${id}` : base
}

export const saveGameStateToLocalStorage = (
  isLatestGame: boolean,
  gameState: StoredGameState
) => {
  const key = scopedKey(isLatestGame ? gameStateKey : archiveGameStateKey)
  localStorage.setItem(key, JSON.stringify(gameState))
}

export const loadGameStateFromLocalStorage = (isLatestGame: boolean) => {
  const key = scopedKey(isLatestGame ? gameStateKey : archiveGameStateKey)
  const state = localStorage.getItem(key)
  return state ? (JSON.parse(state) as StoredGameState) : null
}

const gameStatKey = 'gameStats'

export type GameStats = {
  winDistribution: number[]
  gamesFailed: number
  currentStreak: number
  bestStreak: number
  totalGames: number
  successRate: number
}

export const saveStatsToLocalStorage = (gameStats: GameStats) => {
  localStorage.setItem(gameStatKey, JSON.stringify(gameStats))
}

export const loadStatsFromLocalStorage = () => {
  const stats = localStorage.getItem(gameStatKey)
  return stats ? (JSON.parse(stats) as GameStats) : null
}

export const setStoredIsHighContrastMode = (isHighContrast: boolean) => {
  if (isHighContrast) {
    localStorage.setItem(highContrastKey, '1')
  } else {
    localStorage.removeItem(highContrastKey)
  }
}

export const getStoredIsHighContrastMode = () => {
  const highContrast = localStorage.getItem(highContrastKey)
  return highContrast === '1'
}

// --- Solve-time tracking for the leaderboard ---------------------------------
// Records when the player began a given puzzle (keyed by solutionIndex) so the
// elapsed solve time can be computed at game end. Starts the clock once, on the
// first guess, and is idempotent thereafter.
const startTimeKeyPrefix = 'puzzleStart:'

export const markPuzzleStarted = (day: number) => {
  const key = `${startTimeKeyPrefix}${day}`
  if (!localStorage.getItem(key)) {
    localStorage.setItem(key, Date.now().toString())
  }
}

// Elapsed ms since the puzzle was started, or null if no start was recorded.
export const getPuzzleElapsedMs = (day: number): number | null => {
  const raw = localStorage.getItem(`${startTimeKeyPrefix}${day}`)
  if (!raw) return null
  const start = Number(raw)
  if (!Number.isFinite(start)) return null
  return Math.max(0, Date.now() - start)
}
